import type { QueryClient } from '@tanstack/react-query';
import pino from 'pino';

import { buildTeamsOptions } from '../../../components/pages/wallet/walletHelper';
import { overviewConstants } from '../../../constants/appConstants';
import { getCombinedApprovalsData } from '../../../queries/approvals/approvalQuery';
import { companyFeaturesQuery } from '../../../queries/companyQuery';
import { getOooSettingsQuery, getTeamUsersQuery, selectedTeamIdQuery, teamsQuery } from '../../../queries/teamQuery';
import type { User } from '../../../queries/teamQuery';
import { userQuery } from '../../../queries/userQuery';
import type { OutOfOffice } from '../../../repositories/requestRepository';
import { isCompanyOrSupportAdmin } from '../../../selectors/userSelectors';

const logger = pino();
let teams: { id: string; label: string }[] = [];

export const activityLoader = async ({ queryClient }: { queryClient: QueryClient }) => {
	const teamIdQuery = selectedTeamIdQuery('selectedTeamID');

	let companyFeatures;
	try {
		companyFeatures = await queryClient.ensureQueryData(companyFeaturesQuery);
	} catch (error) {
		logger.error({ error });
	}

	const user = await queryClient.ensureQueryData(userQuery);

	let teamId = [''];
	try {
		teamId = await queryClient.ensureQueryData(teamIdQuery);
	} catch (error) {
		teamId = [''];
		logger.error({ error });
	}

	const userRole = isCompanyOrSupportAdmin(user.roles)
		? overviewConstants.COMPANY_ADMIN
		: user.roles.find(role => role.securityGroupId === String(teamId))?.roleId || '';

	const approvalsQuery = getCombinedApprovalsData(user, teamId?.[0] || '', true);

	const approvalsData = await queryClient.ensureQueryData({
		queryKey: approvalsQuery.queryKey,
		queryFn: approvalsQuery.queryFn,
	});

	let outOfOfficeSettings: OutOfOffice;
	try {
		const currentTeamId = teamId?.[0] || '';
		if (currentTeamId) {
			const oooSettingsQuery = getOooSettingsQuery({
				userId: user.userId,
				teamId: teamId?.[0] || '',
			});
			outOfOfficeSettings = await queryClient.ensureQueryData({
				queryKey: oooSettingsQuery.queryKey,
				queryFn: oooSettingsQuery.queryFn,
			});
		} else {
			outOfOfficeSettings = {} as OutOfOffice;
		}
	} catch (error) {
		logger.error({ error });
		outOfOfficeSettings = {} as OutOfOffice;
	}

	const teamData = await queryClient.ensureQueryData(teamsQuery({ state: 'active' }));
	let allMembersFromAllTeams: User[] = [];
	if (userRole === overviewConstants.COMPANY_ADMIN) {
		teams = buildTeamsOptions({ teams: teamData });
		const allMembersCollections = await Promise.all(
			teamData.map(async team => {
				try {
					const teamMembers = await queryClient.ensureQueryData(getTeamUsersQuery({ teamId: team.id, query: '' }));
					return teamMembers?.users || [];
				} catch {
					return [];
				}
			}),
		);
		const uniqueMembersById = new Map<string, User>();
		for (const member of allMembersCollections.flat()) {
			if (member?.userId && !uniqueMembersById.has(member.userId)) {
				uniqueMembersById.set(member.userId, member);
			}
		}
		allMembersFromAllTeams = [...uniqueMembersById.values()];
	}

	return {
		user,
		userRole,
		teamId,
		approvalsData,
		outOfOfficeSettings,
		companyFeatures,
		teams,
		allMembersFromAllTeams,
	};
};
